import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";

const QuestionLibraryForm = ({ question, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    question: "",
    type: "rating",
    category: "wellbeing",
    description: "",
    tags: "",
    active: true,
  });

  useEffect(() => {
    if (question) {
      setFormData({
        question: question.question || "",
        type: question.type || "rating",
        category: question.category || "wellbeing",
        description: question.description || "",
        tags: (question.tags || []).join(", "),
        active: question.active !== false,
      });
    }
  }, [question]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const tagsArray = formData.tags
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);

    onSave({
      question: formData.question,
      type: formData.type,
      category: formData.category,
      description: formData.description || undefined,
      tags: tagsArray,
      active: formData.active,
    });
  };

  return (
    <Dialog open={true} onOpenChange={onCancel}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900">
            {question ? "Edit Question" : "Create New Question"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div>
            <Label
              htmlFor="question"
              className="text-sm font-semibold text-gray-900"
            >
              Question Text <span className="text-red-500">*</span>
            </Label>
            <Input
              id="question"
              value={formData.question}
              onChange={(e) =>
                setFormData({ ...formData, question: e.target.value })
              }
              placeholder="e.g., How are you feeling today?"
              className="mt-2 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label
                htmlFor="type"
                className="text-sm font-semibold text-gray-900"
              >
                Type <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.type}
                onValueChange={(value) =>
                  setFormData({ ...formData, type: value })
                }
              >
                <SelectTrigger className="mt-2 bg-white border-gray-300 text-gray-900">
                  <SelectValue placeholder="Select question type" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="rating">Rating</SelectItem>
                  <SelectItem value="text">Text</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label
                htmlFor="category"
                className="text-sm font-semibold text-gray-900"
              >
                Category <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.category}
                onValueChange={(value) =>
                  setFormData({ ...formData, category: value })
                }
              >
                <SelectTrigger className="mt-2 bg-white border-gray-300 text-gray-900">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="wellbeing">Wellbeing</SelectItem>
                  <SelectItem value="stress">Stress</SelectItem>
                  <SelectItem value="engagement">Engagement</SelectItem>
                  <SelectItem value="culture">Culture</SelectItem>
                  <SelectItem value="workload">Workload</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label
              htmlFor="description"
              className="text-sm font-semibold text-gray-900"
            >
              Description
            </Label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Optional description or notes about this question"
              className="mt-2 w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
              rows={3}
            />
          </div>

          <div>
            <Label
              htmlFor="tags"
              className="text-sm font-semibold text-gray-900"
            >
              Tags
            </Label>
            <Input
              id="tags"
              value={formData.tags}
              onChange={(e) =>
                setFormData({ ...formData, tags: e.target.value })
              }
              placeholder="Comma-separated tags (e.g., daily, check-in, wellbeing)"
              className="mt-2 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
            />
            <p className="text-sm text-gray-500 mt-1">
              Separate multiple tags with commas
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Switch
              id="active"
              checked={formData.active}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, active: checked })
              }
            />
            <Label
              htmlFor="active"
              className="text-sm font-semibold text-gray-900 cursor-pointer"
            >
              Active (question will be available for use)
            </Label>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="!border-gray-300 !text-gray-700 hover:!bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="!bg-blue-600 hover:!bg-blue-700 !text-white"
            >
              {question ? "Update" : "Create"} Question
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuestionLibraryForm;

